import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const ErrorTooltip = ({ errors, children }) => {
	if (errors.length === 0) return children;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-md w-max z-50">
				{errors.map((err, i) => (
					<div key={i} className="flex items-start gap-1.5 text-xs text-red-400 py-0.5">
						<span className="w-1 h-1 rounded-full bg-red-500 shrink-0 mt-1.5" />
						<span className="break-words">{err.message}</span>
					</div>
				))}
			</TooltipContent>
		</Tooltip>
	);
};

const ParamCell = ({ param, errorTypes = new Set(), fieldErrors = [] }) => {
	if (!param) return <td colSpan={4} className="px-2 py-0.5 text-gray-600 italic font-mono text-xs">&mdash;</td>;
	const hasError = errorTypes.size > 0;
	return (
		<>
			<td className="px-2 py-0.5 font-mono text-xs truncate max-w-[120px]">
				{hasError ? (
					<ErrorTooltip errors={fieldErrors}>
						<span className="text-red-400 decoration-red-400/40 decoration-dashed underline underline-offset-2 cursor-help">
							{param.name}
						</span>
					</ErrorTooltip>
				) : (
					<span className="text-foreground">{param.name}</span>
				)}
			</td>
			<td className={`px-2 py-0.5 font-mono text-xs ${errorTypes.has('PARAM_TYPE') ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>
				{param.type}
			</td>
			<td className={`px-2 py-0.5 font-mono text-xs ${errorTypes.has('PARAM_LOCATION') ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>
				{param.location}
			</td>
			<td className={`px-2 py-0.5 font-mono text-xs text-center ${errorTypes.has('PARAM_REQUIRED') ? 'text-red-400 font-semibold' : 'text-primary'}`}>
				{param.required ? '*' : ''}
			</td>
		</>
	);
};

const ParameterComparisonTable = ({ consumerParams = [], producerParams = [], errors = [] }) => {
	const rows = [];
	const producerUsed = new Set();

	for (const cp of consumerParams) {
		const matchIdx = producerParams.findIndex(
			(pp) => !producerUsed.has(pp.name) && pp.name?.toLowerCase() === cp.name?.toLowerCase()
		);
		if (matchIdx !== -1) {
			producerUsed.add(producerParams[matchIdx].name);
			rows.push({ consumer: cp, producer: producerParams[matchIdx] });
		} else {
			rows.push({ consumer: cp, producer: null });
		}
	}

	for (const pp of producerParams) {
		if (!producerUsed.has(pp.name)) {
			rows.push({ consumer: null, producer: pp });
		}
	}

	if (rows.length === 0) {
		return <div className="text-xs text-muted-foreground italic px-2 py-1">No parameters</div>;
	}

	const getFieldErrors = (fieldName) =>
		errors.filter(
			(e) =>
				e.section === 'request.parameters' &&
				(e.fieldPath ? e.fieldPath === `/${fieldName}` : e.fieldName?.toLowerCase() === fieldName?.toLowerCase())
		);

	const setLevelErrors = errors.filter((e) => e.section === 'request.parameters' && !e.fieldName);

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse">
				<thead>
					<tr className="text-[10px] text-muted-foreground uppercase tracking-wider bg-muted/10">
						<th className="px-2 py-1 text-left font-semibold">Name</th>
						<th className="px-2 py-1 text-left font-semibold">Type</th>
						<th className="px-2 py-1 text-left font-semibold">Loc</th>
						<th className="px-2 py-1 text-center font-semibold w-8">Req</th>
						<th className="px-2 py-1 text-left font-semibold">Name</th>
						<th className="px-2 py-1 text-left font-semibold">Type</th>
						<th className="px-2 py-1 text-left font-semibold">Loc</th>
						<th className="px-2 py-1 text-center font-semibold w-8">Req</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => {
						const fieldName = row.consumer?.name || row.producer?.name;
						const fieldErrors = getFieldErrors(fieldName);
						const errorTypes = new Set(fieldErrors.map((e) => e.errorType));

						return (
							<tr key={i}>
								<ParamCell param={row.consumer} errorTypes={errorTypes} fieldErrors={fieldErrors} />
								<ParamCell param={row.producer} errorTypes={errorTypes} fieldErrors={fieldErrors} />
							</tr>
						);
					})}
				</tbody>
			</table>

			{setLevelErrors.length > 0 && (
				<div className="px-2 py-1 border-t border-border/10">
					{setLevelErrors.map((err, i) => (
						<div key={i} className="flex items-center gap-1 text-[10px] text-red-400/70">
							<span className="w-1 h-1 rounded-full bg-red-500/50 shrink-0" />
							{err.message}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default ParameterComparisonTable;
